export type Unit = 'pc' | 'kg' | 'g' | 'l' | 'ml' | 'pack';
export type Role = 'owner' | 'manager' | 'cashier' | 'stock_keeper' | 'accountant';
/** KYC documents are optional for now: the setup step, checklist and review section stay hidden. */
export const KYC_ENABLED = false;

export type StoreStatus = 'pending' | 'approved' | 'paused' | 'rejected' | 'deactivated';
export type PayMethod = 'cash' | 'upi' | 'card' | 'credit';

export interface Product {
  id: number;
  sku: string;
  name: string;
  name_local?: string | null; // second-language name (e.g. Tamil), searchable
  category: string;
  unit: Unit;
  price: number;      // paise, GST inclusive
  mrp: number | null;
  cost: number | null;
  tax_bp: number;     // 500 = 5%
  hsn: string | null;
  barcodes: string[];
  plu: number | null; // weighing-scale PLU
  reorder: number | null; // milli-units
  active: boolean;
  version: number;
}

/** Packed bill line: [productId, qtyMilli, unitPricePaise, lineDiscountPaise, taxBp] */
export type BillLine = [number, number, number, number, number];
/** Packed payment: [method, amountPaise, reference?] */
export type Payment = [PayMethod, number, string?];

export interface Bill {
  id: string;          // UUIDv7
  bill_no: string;     // T01-000123
  dev_no: number;      // device counter (dedupe)
  ts: number;
  cashier: string;
  cashier_id?: string | null;
  customer: string | null; // phone
  customer_id?: string | null;
  kind: 0 | 1;         // 0 sale, 1 return
  items: BillLine[];
  payments: Payment[];
  subtotal: number;
  discount: number;
  tax: number;
  round: number;
  total: number;
  offers?: Array<[number, number]>; // [offerId, saved paise]
  approvals?: Array<[string, string]>; // [action, approver] e.g. ['price', 'Asha']
  gstin?: string | null;               // buyer GSTIN for a B2B invoice
}

export type MovementKind = 'grn' | 'adjust' | 'waste' | 'produce' | 'xfer_out' | 'xfer_in' | 'supplier_return';
/** Packed movement line: [productId, signedQtyMilli, costPaise?] */
export type MovementLine = [number, number, number?];

export interface Movement {
  id: string;
  dev_no: number;      // device movement counter (dedupe)
  kind: MovementKind;
  ref: string | null;
  note: string | null;
  lines: MovementLine[];
  ts: number;
  meta?: MovementMeta;
}

/** Optional details carried by a movement. */
export interface MovementMeta {
  supplier_id?: number;
  po_id?: string;
  amount?: number;                     // paise owed to the supplier for this receipt (or credited back on a return)
  to_store?: string;                   // transfer out → this store
  from_store?: string;                 // transfer in ← this store
  expiry?: Array<[number, number]>;    // [productId, expiry epoch ms] for batches
}

export interface Supplier {
  id: number;
  name: string;
  phone: string | null;
  gstin: string | null;
  address: string | null;
  terms_days: number | null;
  balance: number;   // paise we owe
  active: boolean;
  version: number;
}

export type PoStatus = 'open' | 'received' | 'cancelled';
export interface PurchaseOrder {
  id: string;
  supplier_id: number;
  po_no: string;
  status: PoStatus;
  lines: Array<[number, number, number]>; // [productId, qtyMilli, costPaise]
  total: number;
  note: string | null;
  expected_at: number | null;
  created_at: number;
  created_by: string | null;
  received_at: number | null;
}

export interface Batch { id: number; product_id: number; qty: number; expiry: number; created_at: number }

/**
 * Subscription plans, priced per store like Sense Fresh (paise; GST extra). Yearly = 10 months, so
 * 2 months free. `null` = unlimited. `terminals`: tills / phones that bill. `staff`: active staff logins
 * besides the owner. `stores`: stores one owner may have (the best plan among their stores decides).
 */
export type PlanId = 'starter' | 'growth' | 'pro' | 'ultra';
export type BillingCycle = 'monthly' | 'yearly';
export interface Plan {
  id: PlanId;
  name: string;
  tagline: string;
  monthly: number;
  yearly: number;
  terminals: number | null;
  staff: number | null;
  stores: number;
  features: string[];
  highlight?: boolean;
}
export const PLANS: Plan[] = [
  {
    id: 'starter', name: 'Starter', tagline: 'Free billing on one phone', monthly: 0, yearly: 0, terminals: 1, staff: 0, stores: 1,
    features: ['Free forever', 'Data stays on this phone (no cloud backup)', 'GST billing & receipts, UPI QR', 'Stock, khata, offers & reports', 'Barcode scanner & weighing scale'],
  },
  {
    id: 'growth', name: 'Growth', tagline: 'Shops with a team', monthly: 14_900, yearly: 1_49_000, terminals: null, staff: 4, stores: 1, highlight: true,
    features: ['Everything in Starter', 'Cloud backup & sync', 'Billing on every till and phone', 'Manager, cashier & stock keeper logins', 'Roles & access for your team', 'Reports from every till'],
  },
  {
    id: 'pro', name: 'Pro', tagline: 'Busy stores with a second branch', monthly: 19_900, yearly: 1_99_000, terminals: null, staff: null, stores: 2,
    features: ['Everything in Growth', 'Up to 2 stores', 'Unlimited staff logins', 'Stock transfers between stores', 'Priority support'],
  },
  {
    id: 'ultra', name: 'Ultra', tagline: 'Chains with many outlets', monthly: 29_900, yearly: 2_99_000, terminals: null, staff: null, stores: 10,
    features: ['Everything in Pro', 'Up to 10 stores', 'Unlimited staff logins', 'Priority support', 'Early access to new features'],
  },
];
export const PLAN_IDS = PLANS.map((p) => p.id);
export const isPlanId = (id: unknown): id is PlanId => typeof id === 'string' && (PLAN_IDS as string[]).includes(id);
export const planById = (id: string | null | undefined): Plan => PLANS.find((p) => p.id === id) ?? PLANS[1];
/** Kept for older code: plans by id. */
export const PLAN_PRICES: Record<PlanId, Plan> = Object.fromEntries(PLANS.map((p) => [p.id, p])) as Record<PlanId, Plan>;
/** "1 store · 1 billing till · Owner only", "Up to 2 stores · Unlimited staff logins" */
export function planLimits(p: Plan): string {
  const lim = (n: number | null, one: string, many: string) => (n == null ? `Unlimited ${many}` : `${n} ${n === 1 ? one : many}`);
  if (p.id === 'starter') return '1 store · 1 phone · Owner only · phone-only';
  return [p.stores === 1 ? '1 store' : `Up to ${p.stores} stores`, ...(p.terminals != null ? [lim(p.terminals, 'billing till', 'billing tills')] : []), p.staff === 0 ? 'Owner only' : lim(p.staff, 'staff login', 'staff logins')].join(' · ');
}
/** Whole months saved by paying yearly (₹1,490/yr vs ₹149/mo → 2). */
export const yearlyMonthsFree = (p: Plan) => (p.monthly > 0 ? Math.max(0, Math.round(12 - p.yearly / p.monthly)) : 0);

export interface SessionClaims {
  uid: string;
  tid: string;
  sid: string;
  did: string;
  role: Role;
  name: string;
  plan: string;
  st?: string;          // store status when the token was issued
  acc?: AccessRule;     // staff access rule, checked on every request without a database read
  pa?: boolean;         // platform admin (no store)
}

/** When a staff member may use the store: until `exp`, and inside weekly windows (store local time). */
export interface AccessRule {
  exp?: number | null;                          // epoch ms
  tz?: number;                                  // store UTC offset in minutes (India 330)
  days?: Array<[number, number] | null> | null; // Sunday first: [fromMinute, toMinute] or null = closed
}

export type AccessProblem = 'expired' | 'outside_hours' | null;
export function accessProblem(a: AccessRule | null | undefined, now = Date.now()): AccessProblem {
  if (!a) return null;
  if (a.exp && now > a.exp) return 'expired';
  if (a.days?.length === 7) {
    const local = new Date(now + (a.tz ?? 330) * 60000);
    const w = a.days[local.getUTCDay()];
    const m = local.getUTCHours() * 60 + local.getUTCMinutes();
    if (!w || m < w[0] || m >= w[1]) return 'outside_hours';
  }
  return null;
}

export interface StaffMember {
  id: string;
  name: string;
  role: Role;
  pin_hash: string | null; // PBKDF2-SHA256, hashed on the device; the server never sees a PIN
  pin_salt: string | null;
  access_expires_at?: number | null;
  access_schedule?: Array<[number, number] | null> | null;
}

export interface Customer {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  notes: string | null;
  credit_limit: number | null; // paise
  balance: number;             // paise owed on khata
  points: number;
  total_spent: number;
  visits: number;
  last_visit: number | null;
}

/** Cash register shift on one terminal. */
export interface Shift {
  id: string;
  opened_at: number;
  opened_by: string;
  float: number;                                 // opening cash, paise
  moves: Array<[number, number, string]>;        // [ts, ±paise, reason] cash in / out
  closed_at: number | null;
  closed_by: string | null;
  counted: number | null;                        // cash counted at close
  expected: number | null;                       // float + cash sales − refunds ± moves
  denominations: Record<string, number> | null;  // note/coin → count
  totals: { bills: number; sales: number; returns: number; byMethod: Record<string, number> } | null;
}
